/**
 * Premier producers — farms a processor vouches for on their public buyer page
 * (`/processors/:slug`). Selections are stored on
 * `LandingPageSettings.premierProducers` as `{ producerId, badge }`, where
 * `badge` is one of the stable keys below; the label is what renders as a chip
 * on that producer's animal cards in the merged top section.
 *
 * `in_house` is also the chip automatically applied to the processor's OWN
 * animals. A premier producer assigned `in_house` renders identically — a
 * deliberate choice: the processor presents that farm's animals as their own.
 *
 * Only the stable keys are persisted, so labels can be reworded without a
 * migration; do NOT change an existing key (it would orphan stored badges).
 */
export const PREMIER_PRODUCER_BADGES = [
  'recommended',
  'partner_farm',
  'featured_farm',
  'in_house',
] as const;

export type PremierProducerBadge = (typeof PREMIER_PRODUCER_BADGES)[number];

export const DEFAULT_PREMIER_PRODUCER_BADGE: PremierProducerBadge =
  'recommended';

/** Cap on premier producers per landing page (mirrors MAX_BUYER_DESCRIPTOR_CHIPS). */
export const MAX_PREMIER_PRODUCERS = 5;

/** Human-facing chip copy per badge key. */
export const PREMIER_PRODUCER_BADGE_LABELS: Record<
  PremierProducerBadge,
  string
> = {
  recommended: 'Recommended',
  partner_farm: 'Partner farm',
  featured_farm: 'Featured farm',
  in_house: 'In house',
};

/**
 * Returns the display label for a badge key, or undefined if unknown.
 * @param value
 */
export function premierProducerBadgeLabel(value: string): string | undefined {
  return (PREMIER_PRODUCER_BADGE_LABELS as Record<string, string>)[value];
}
